import { service } from '@ember/service';

import moment from 'moment';

import type { APIData, Service } from 'webapp-types';
import { PlanType, PromotionIdentifier, PromotionType } from 'webapp/enums/enums';
import { getFormattedPrice, getFormattedTime, getIntervalTime } from 'webapp/utils/number';

import Serviced from 'webapp/models-custom/serviced';

export interface BillingDescriptions {
	monthly: string;
	quarterly: string;
	yearly: string;
}

export default class Promotion extends Serviced {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('timer') declare timerService: Service.Timer;
	@service('billing') declare billingService: Service.Billing;

	// --- BE fields, Sync with APIData.Billing.Promotions.Item
	id: PromotionIdentifier;
	type: PromotionType;
	name: string;
	description: string;
	productIds: string[] | null;
	productName: string | null;
	activeUntil: number | null;
	discount: APIData.Billing.Promotions.Discount | null;
	billingDescriptions: BillingDescriptions | null = null;

	// --- BE fields

	constructor(model: APIData.Billing.Promotions.Item) {
		super();

		this.id = model.id;
		this.type = model.type;
		this.name = model.name;
		this.description = model.description;
		this.billingDescriptions = model.billingDescriptions;
		this.productIds = model.productIds;
		this.productName = model.productName;
		this.activeUntil = model.activeUntil;
		this.discount = model.discount;
	}

	// --- common
	get isTrial() {
		return this.type === PromotionType.TRIALING_FEATURES;
	}

	get isDiscount() {
		return this.type === PromotionType.DISCOUNT;
	}

	get formattedActiveUntilMMMDo(): string {
		let time = '';
		if (this.activeUntil) {
			time = getFormattedTime(
				this.activeUntil,
				this.currentUserService.model?.timezone,
				'MMM Do',
			);
		}
		return time;
	}

	get discountAmmount() {
		return this.discount?.amount || 0;
	}

	get discountAmmountFormatted(): string {
		return getFormattedPrice(this.discountAmmount);
	}

	get discountDurationInMonths() {
		return this.discount?.durationInMonths || 0;
	}

	get totalDiscountAmmount() {
		return this.discountAmmount * this.discountDurationInMonths;
	}

	get totalDiscountAmmountFormatted(): string {
		return getFormattedPrice(this.totalDiscountAmmount);
	}

	get activeUntilMoment() {
		return this.activeUntil ? moment.unix(this.activeUntil) : null;
	}

	get isCurrent() {
		/*
			Promotions can be *active* or *available(an option)* based on where they are returned in APIData.Billing.Promotions.Response
			This flag is intentionally not named isActive, because it doesn't mean that the promotion is activated for a cretain product (plan, addon) or feature

			This flag means that the promotion hasn't expired yet (whether it's active or only available)
		*/
		return !!this.activeUntil && this.timerService.currentTs < this.activeUntil;
	}

	get daysRemaining() {
		return this.activeUntilMoment?.diff(moment.unix(this.timerService.currentTs), 'days') ?? 0;
	}

	get timeLeftText() {
		const timeLeftTS = (this.activeUntil ?? 0) - this.timerService.currentTs;
		let timeLeftText = '';

		if (timeLeftTS > 0) {
			timeLeftText = `${getIntervalTime(timeLeftTS, 'seconds', true)}`;
		}

		return timeLeftText;
	}

	// --- common

	// --- Trial addon

	get isTrialAddon() {
		return this.id === PromotionIdentifier.TRIAL_ADDON;
	}

	// --- Trial addon

	// --- 	Trial higher plan
	get isHigherPlanAutomatedTrial() {
		return this.id === PromotionIdentifier.TRIAL_HIGHER_PLAN;
	}

	// --- 	Trial higher plan

	// --- 	Trial higher plan offer
	get isHigherPlanAutomatedTrialOffer() {
		return this.id === PromotionIdentifier.TRIAL_HIGHER_PLAN_OFFER;
	}

	// --- 	Trial higher plan offer

	// --- Growth plan promo (May 2024)
	get isGrowthFeatureTrial() {
		// isTrial true
		return this.id === PromotionIdentifier.TRIAL_GROWTH_FEATURES;
	}

	get isGrowthPlanDiscount() {
		// isDiscount true
		return this.id === PromotionIdentifier.GROWTH_PROMOTION;
	}
	// --- Growth plan promo (May 2024)

	isForPlanType(planType: PlanType) {
		const planTypeName = this.billingService.agencyPricing ? `agency-${planType}` : planType;
		return this.productIds?.includes(planTypeName);
	}
}
